// WHAT: Workspace card component for displaying workspace with aspects
// WHY:  Encapsulates workspace display UI with expand/collapse functionality
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: UI components (Card, Button, Badge, Heading), AspectCard component
// RISKS:
//   - NOTE: Expansion state is managed externally

import type { AspectConfig } from "../types";

import { ChevronDown, ChevronUp } from "lucide-react";

import { Card, Button, Badge, Heading } from "@/components/ui";

import { AspectCard } from "./AspectCard";

/**
 * WHAT:
 *   Component for displaying a workspace with its aspects.
 *
 * WHY:
 *   Provides reusable UI for workspace display with expand/collapse functionality.
 *
 * PROPS:
 *   - workspaceName: Name of the workspace
 *   - aspects: Record of aspect configurations
 *   - isExpanded: Whether the workspace is expanded
 *   - onToggleExpansion: Callback to toggle expansion state
 *   - onUpdateHours: Callback when aspect hours change
 *   - onUpdateStatus: Callback when aspect status changes
 *   - onUpdateColor: Callback when aspect color changes
 *   - onToggleBoolean: Callback when aspect boolean flags are toggled
 *
 * BEHAVIOR:
 *   - Shows first 4 aspects by default, all when expanded
 *   - Displays expand/collapse button if more than 4 aspects exist
 *   - Renders AspectCard for each visible aspect
 */
interface WorkspaceCardProperties {
  aspects: Record<string, AspectConfig>;
  isExpanded: boolean;
  onToggleBoolean: (aspectName: string, field: "carryover" | "count_hours" | "count_plan") => void;
  onToggleExpansion: () => void;
  onUpdateColor: (aspectName: string, color: string) => void;
  onUpdateHours: (aspectName: string, hours: string) => void;
  onUpdateStatus: (aspectName: string, status: string) => void;
  workspaceName: string;
}

/**
 * WHAT:
 *   React component that renders a card for a single TimeTracQ workspace.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for an individual workspace.
 *
 * @param root0 - Component props
 *
 * @param root0.aspects - Map of aspect configurations
 *
 * @param root0.isExpanded - Whether the workspace is expanded
 *
 * @param root0.onToggleBoolean - Callback to toggle boolean properties
 *
 * @param root0.onToggleExpansion - Callback to toggle workspace expansion
 *
 * @param root0.onUpdateColor - Callback to update aspect color
 *
 * @param root0.onUpdateHours - Callback to update aspect hours
 *
 * @param root0.onUpdateStatus - Callback to update aspect status
 *
 * @param root0.workspaceName - Name of the workspace
 *
 * @returns {JSX.Element} Workspace card component
 *
 */
export function WorkspaceCard({
  aspects,
  isExpanded,
  onToggleBoolean,
  onToggleExpansion,
  onUpdateColor,
  onUpdateHours,
  onUpdateStatus,
  workspaceName,
}: Readonly<WorkspaceCardProperties>) {
  const aspectEntries = Object.entries(aspects);
  const visibleAspects = isExpanded ? aspectEntries : aspectEntries.slice(0, 4);
  const hasMoreAspects = aspectEntries.length > 4;

  return (
    <Card className="bg-slate-800/50 border-slate-700/50 overflow-hidden p-0" variant="default">
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <Heading className="text-lg font-semibold text-white" level={4}>
            {workspaceName}
          </Heading>
          <Badge size="sm" style="subtle" variant="default">
            {aspectEntries.length} Aspects
          </Badge>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {visibleAspects.map(([aspectName, aspect]) => (
          <AspectCard
            aspect={aspect}
            aspectName={aspectName}
            key={aspectName}
            onToggleBoolean={(field) => {
              onToggleBoolean(aspectName, field);
            }}
            onUpdateColor={(color) => {
              onUpdateColor(aspectName, color);
            }}
            onUpdateHours={(hours) => {
              onUpdateHours(aspectName, hours);
            }}
            onUpdateStatus={(status) => {
              onUpdateStatus(aspectName, status);
            }}
          />
        ))}

        {hasMoreAspects ? (
          <div className="pt-2">
            <Button className="w-full" onClick={onToggleExpansion} size="md" variant="ghost">
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  {aspectEntries.length - 4} more aspects
                </>
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
